import type { Env } from '@dma/config';
import { ConsoleLogger, EventBus, MemoryCache, FlagProvider } from '@dma/shared';
import { getSupabaseClient } from '@dma/data-access';
import { generateRequestId } from '@dma/utils';
import { GetHomeHeroContentUseCase, StaticHomeContentSource } from '../features/home';
import { SubmitContactMessageUseCase, SupabaseContactMessageRepository } from '../features/contact';
import { GetAboutContentUseCase, StaticAboutContentSource } from '../features/about';
import {
  GetServicesUseCase,
  GetServiceBySlugUseCase,
  StaticServicesSource
} from '../features/services';
import {
  GetProjectsUseCase,
  GetProjectBySlugUseCase,
  StaticProjectsSource
} from '../features/projects';
import { GetGalleryUseCase, StaticGallerySource } from '../features/gallery';
import {
  GetMineralsUseCase,
  GetMineralBySlugUseCase,
  StaticMineralsSource
} from '../features/minerals';
import {
  SignInUseCase,
  SignOutUseCase,
  GetCurrentSessionUseCase,
  SupabaseAuthRepository
} from '../features/admin';
import {
  GetScientificEntriesUseCase,
  GetScientificEntryBySlugUseCase,
  GetAdminScientificEntriesUseCase,
  GetScientificEntryByIdUseCase,
  SaveScientificEntryUseCase,
  DeleteScientificEntryUseCase,
  SupabaseScientificEntryRepository
} from '../features/scientific-content';
import {
  GetArticlesUseCase,
  GetArticleBySlugUseCase,
  GetAdminArticlesUseCase,
  GetArticleByIdUseCase,
  SaveArticleUseCase,
  DeleteArticleUseCase,
  SupabaseArticleRepository
} from '../features/news';
import {
  GetActiveBannerUseCase,
  GetBannerForAdminUseCase,
  SaveBannerUseCase,
  SupabaseBannerRepository
} from '../features/banner';
import { UploadImageUseCase, SupabaseMediaRepository } from '../features/media';

/**
 * Composition Root per docs/architecture/01-dependency-injection-strategy.md.
 * This is the ONLY place in the app that instantiates concrete infrastructure
 * implementations. Every other module receives these through props/context,
 * never by importing the concrete class directly.
 */
export function createCompositionRoot(env: Env) {
  const requestId = generateRequestId();
  const logger = new ConsoleLogger(requestId);
  const eventBus = new EventBus();
  const cache = new MemoryCache();
  const flagProvider = new FlagProvider(import.meta.env);
  const supabaseClient = getSupabaseClient(env);

  const homeContentSource = new StaticHomeContentSource();
  const getHomeHeroContentUseCase = new GetHomeHeroContentUseCase(homeContentSource, logger);

  const contactMessageRepository = new SupabaseContactMessageRepository(supabaseClient);
  const submitContactMessageUseCase = new SubmitContactMessageUseCase(
    contactMessageRepository,
    eventBus,
    logger
  );

  const aboutContentSource = new StaticAboutContentSource();
  const getAboutContentUseCase = new GetAboutContentUseCase(aboutContentSource, logger);

  const servicesSource = new StaticServicesSource();
  const getServicesUseCase = new GetServicesUseCase(servicesSource, logger);
  const getServiceBySlugUseCase = new GetServiceBySlugUseCase(servicesSource, logger);

  const projectsSource = new StaticProjectsSource();
  const getProjectsUseCase = new GetProjectsUseCase(projectsSource, logger);
  const getProjectBySlugUseCase = new GetProjectBySlugUseCase(projectsSource, logger);

  const gallerySource = new StaticGallerySource();
  const getGalleryUseCase = new GetGalleryUseCase(gallerySource, logger);

  const mineralsSource = new StaticMineralsSource();
  const getMineralsUseCase = new GetMineralsUseCase(mineralsSource, logger);
  const getMineralBySlugUseCase = new GetMineralBySlugUseCase(mineralsSource, logger);

  const authRepository = new SupabaseAuthRepository(supabaseClient);
  const signInUseCase = new SignInUseCase(authRepository, logger);
  const signOutUseCase = new SignOutUseCase(authRepository, logger);
  const getCurrentSessionUseCase = new GetCurrentSessionUseCase(authRepository);

  const scientificEntryRepository = new SupabaseScientificEntryRepository(supabaseClient);
  const getScientificEntriesUseCase = new GetScientificEntriesUseCase(scientificEntryRepository, logger);
  const getScientificEntryBySlugUseCase = new GetScientificEntryBySlugUseCase(scientificEntryRepository, logger);
  const getAdminScientificEntriesUseCase = new GetAdminScientificEntriesUseCase(scientificEntryRepository, logger);
  const getScientificEntryByIdUseCase = new GetScientificEntryByIdUseCase(scientificEntryRepository);
  const saveScientificEntryUseCase = new SaveScientificEntryUseCase(scientificEntryRepository, logger);
  const deleteScientificEntryUseCase = new DeleteScientificEntryUseCase(scientificEntryRepository, logger);

  const articleRepository = new SupabaseArticleRepository(supabaseClient);
  const getArticlesUseCase = new GetArticlesUseCase(articleRepository, logger);
  const getArticleBySlugUseCase = new GetArticleBySlugUseCase(articleRepository, logger);
  const getAdminArticlesUseCase = new GetAdminArticlesUseCase(articleRepository, logger);
  const getArticleByIdUseCase = new GetArticleByIdUseCase(articleRepository);
  const saveArticleUseCase = new SaveArticleUseCase(articleRepository, logger);
  const deleteArticleUseCase = new DeleteArticleUseCase(articleRepository, logger);

  const bannerRepository = new SupabaseBannerRepository(supabaseClient);
  const getActiveBannerUseCase = new GetActiveBannerUseCase(bannerRepository);
  const getBannerForAdminUseCase = new GetBannerForAdminUseCase(bannerRepository);
  const saveBannerUseCase = new SaveBannerUseCase(bannerRepository, logger);

  const mediaRepository = new SupabaseMediaRepository(supabaseClient);
  const uploadImageUseCase = new UploadImageUseCase(mediaRepository, logger);

  return {
    logger,
    eventBus,
    cache,
    flagProvider,
    supabaseClient,
    getHomeHeroContentUseCase,
    submitContactMessageUseCase,
    getAboutContentUseCase,
    getServicesUseCase,
    getServiceBySlugUseCase,
    getProjectsUseCase,
    getProjectBySlugUseCase,
    getGalleryUseCase,
    getMineralsUseCase,
    getMineralBySlugUseCase,
    signInUseCase,
    signOutUseCase,
    getCurrentSessionUseCase,
    getScientificEntriesUseCase,
    getScientificEntryBySlugUseCase,
    getAdminScientificEntriesUseCase,
    getScientificEntryByIdUseCase,
    saveScientificEntryUseCase,
    deleteScientificEntryUseCase,
    getArticlesUseCase,
    getArticleBySlugUseCase,
    getAdminArticlesUseCase,
    getArticleByIdUseCase,
    saveArticleUseCase,
    deleteArticleUseCase,
    getActiveBannerUseCase,
    getBannerForAdminUseCase,
    saveBannerUseCase,
    uploadImageUseCase
  };
}

export type CompositionRoot = ReturnType<typeof createCompositionRoot>;
